import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuthContext } from "@/hooks/useAuthContext";
import {
  isValidSignupPhone,
  stashSignupPhone,
} from "@/hooks/useAuth";
import { Navigate, useSearchParams } from "react-router";
import { HelpCircle, Eye, EyeOff } from "lucide-react";
import { Link } from "react-router";
import { useGetLatestTermsOfServiceQuery, useUpdateUserTosAcceptanceMutation } from "@/redux/api/legal.rtkq";

const GoogleMark = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

const LoginPage = () => {
  const {
    user,
    loadingUserData,
    signIn,
    signUp,
    signInWithGoogle,
    resetPassword,
    needsPhoneForRegistration,
    finishRegistrationWithPhone,
  } = useAuthContext();
  const [isSignUp, setIsSignUp] = useState(false);
  const [signupMethod, setSignupMethod] = useState<"" | "email">("");
  const [tosAccepted, setTosAccepted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    fullName: "",
    phoneNumber: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const { data: latestTos } = useGetLatestTermsOfServiceQuery(undefined, {
    skip: !isSignUp,
  });
  const [updateTosAcceptance] = useUpdateUserTosAcceptanceMutation();

  const [searchParams] = useSearchParams();
  const redirectParam = searchParams.get("redirect");
  const unsafeRedirect =
    !redirectParam ||
    redirectParam === "/unauthorized" ||
    redirectParam === "/super-admin" ||
    redirectParam.startsWith("/super-admin/");
  const redirectTo = unsafeRedirect ? "/" : redirectParam;

  if (!loadingUserData && user && !needsPhoneForRegistration) {
    return <Navigate to={redirectTo} replace />;
  }

  if (loadingUserData) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg">טוען...</div>
      </div>
    );
  }

  const phoneOk = isValidSignupPhone(formData.phoneNumber);
  const signupReady = phoneOk && tosAccepted;
  const emailPath = isSignUp && signupMethod === "email";
  const showMethodChooser = isSignUp && !needsPhoneForRegistration && !emailPath;

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);
    setSuccessMessage(null);
  };

  const requirePhoneOrSetError = (): string | null => {
    const phone = formData.phoneNumber.trim();
    if (!phone) {
      setError("מספר טלפון שדה חובה");
      return null;
    }
    if (!isValidSignupPhone(phone)) {
      setError("מספר טלפון לא תקין");
      return null;
    }
    return phone;
  };

  const storeToSAcceptance = async (tosVersion: string) => {
    try {
      await updateTosAcceptance({
        version: tosVersion,
        ipAddress: undefined,
      }).unwrap();
    } catch (storeError) {
      console.error("Failed to store ToS acceptance:", storeError);
    }
  };

  const signupGateMessage = () => {
    if (signupReady) return "";
    if (!phoneOk && !tosAccepted) {
      return "הזן מספר טלפון ואשר את התנאים, ואז בחר שיטת הרשמה.";
    }
    if (!phoneOk) return "הזן מספר טלפון כדי להמשיך.";
    return "אשר את תנאי השימוש כדי להמשיך.";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      if (needsPhoneForRegistration) {
        const phone = requirePhoneOrSetError();
        if (!phone) {
          setIsSubmitting(false);
          return;
        }
        const result = await finishRegistrationWithPhone(phone);
        if (!result.ok) {
          setError(
            result.error === "invalid_phone"
              ? "מספר טלפון לא תקין"
              : "לא הצלחנו להשלים את ההרשמה. נסה שוב.",
          );
        }
        setIsSubmitting(false);
        return;
      }

      if (isSignUp) {
        if (!latestTos?.version) {
          setError("לא ניתן לטעון את תנאי השימוש. אנא נסה שוב.");
          setIsSubmitting(false);
          return;
        }
        if (!tosAccepted) {
          setError("יש לסמן הסכמה לתנאי השימוש ומדיניות הפרטיות");
          setIsSubmitting(false);
          return;
        }
        if (!formData.fullName.trim()) {
          setError("שם מלא, אימייל וסיסמה הם שדות חובה.");
          setIsSubmitting(false);
          return;
        }

        const phone = requirePhoneOrSetError();
        if (!phone) {
          setIsSubmitting(false);
          return;
        }
        stashSignupPhone(phone);

        const result = await signUp({
          email: formData.email,
          password: formData.password,
          displayName: formData.fullName,
        });

        if (result.error) {
          setError(getHebrewErrorMessage(result.error.message));
        } else if (result.user) {
          storeToSAcceptance(latestTos.version);
        }
      } else {
        const result = await signIn(formData.email, formData.password);
        if (result.error) {
          setError(getHebrewErrorMessage(result.error.message));
        }
      }
    } catch (err) {
      console.log(err);
      setError("אירעה שגיאה בלתי צפויה");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsSubmitting(true);
    setError(null);
    setSuccessMessage(null);

    try {
      if (isSignUp) {
        if (!latestTos?.version) {
          setError("לא ניתן לטעון את תנאי השימוש. אנא נסה שוב.");
          setIsSubmitting(false);
          return;
        }
        if (!tosAccepted) {
          setError("יש לסמן הסכמה לתנאי השימוש ומדיניות הפרטיות");
          setIsSubmitting(false);
          return;
        }
        const phone = requirePhoneOrSetError();
        if (!phone) {
          setIsSubmitting(false);
          return;
        }
        stashSignupPhone(phone);
      } else if (formData.phoneNumber.trim()) {
        stashSignupPhone(formData.phoneNumber);
      }

      const result = await signInWithGoogle();
      if (result.error) {
        setError(getHebrewErrorMessage(result.error.message));
      } else if (result.user && isSignUp && latestTos) {
        storeToSAcceptance(latestTos.version);
      }
    } catch (err) {
      console.error(err);
      setError("אירעה שגיאה בהתחברות עם Google");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!formData.email) {
      setError("נא להזין כתובת אימייל");
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const result = await resetPassword(formData.email);
      if (result.error) {
        setError(getHebrewErrorMessage(result.error.message));
      } else {
        setSuccessMessage("נשלח אימייל לאיפוס סיסמה. אנא בדוק את תיבת הדואר שלך");
        setShowForgotPassword(false);
      }
    } catch (err) {
      console.error(err);
      setError("אירעה שגיאה בשליחת אימייל לאיפוס סיסמה");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getHebrewErrorMessage = (errorMessage: string) => {
    if (errorMessage.includes("user-not-found")) {
      return "משתמש לא נמצא";
    }
    if (errorMessage.includes("wrong-password")) {
      return "סיסמה שגויה";
    }
    if (errorMessage.includes("email-already-in-use")) {
      return "כתובת האימייל כבר בשימוש";
    }
    if (errorMessage.includes("weak-password")) {
      return "הסיסמה חלשה מדי";
    }
    if (errorMessage.includes("invalid-email")) {
      return "כתובת אימייל לא תקינה";
    }
    return "אירעה שגיאה בהתחברות";
  };

  const resetAuthMode = (nextSignUp: boolean) => {
    setIsSignUp(nextSignUp);
    setSignupMethod("");
    setTosAccepted(false);
    setError(null);
    setSuccessMessage(null);
    setShowForgotPassword(false);
    setFormData({ email: "", password: "", fullName: "", phoneNumber: "" });
  };

  const inputClass =
    "text-right h-12 md:h-14 px-3 md:px-4 py-2 md:py-3 rounded-lg border-gray-200 focus:border-blue-400 focus:ring-blue-400 placeholder:opacity-50";

  return (
    <div className="min-h-screen flex">
      <div className="w-full lg:w-2/5 flex items-center justify-center p-4 md:p-6 lg:p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="text-center mb-8 md:mb-12">
            <h1 className="text-3xl md:text-4xl font-semibold mb-4 md:mb-6">
              <span style={{ color: "#1F1F68" }}>Legalix.</span>
              <span className="gradient-ai">AI</span>
            </h1>
            <h2 className="text-xl md:text-2xl font-medium mb-2 text-slate-600">
              {needsPhoneForRegistration
                ? "השלם את ההרשמה"
                : isSignUp
                  ? "צור חשבון חדש"
                  : "התחבר לחשבון שלך"}
            </h2>
            {needsPhoneForRegistration && (
              <p className="text-sm text-slate-500">
                נדרש מספר טלפון כדי לסיים את יצירת החשבון
              </p>
            )}
            {isSignUp && !needsPhoneForRegistration && (
              <p className="text-sm text-slate-500">
                הזן טלפון, אשר תנאים ובחר איך ליצור את החשבון
              </p>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6 text-zinc-400">
            {(isSignUp || needsPhoneForRegistration) && (
              <div className="space-y-2">
                <Label htmlFor="phoneNumber" className="text-right block font-medium">
                  מספר טלפון
                  <span className="text-red-600 font-bold ms-1">*</span>
                  <span className="text-red-600 text-xs font-medium ms-1">חובה</span>
                </Label>
                <Input
                  id="phoneNumber"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="מספר הטלפון שלך"
                  value={formData.phoneNumber}
                  onChange={(e) => handleInputChange("phoneNumber", e.target.value)}
                  required
                  className={inputClass}
                  dir="rtl"
                />
                {isSignUp && !needsPhoneForRegistration && (
                  <p className="text-xs text-slate-500 text-right">
                    חובה לכל הרשמה — גם Google וגם אימייל.
                  </p>
                )}
              </div>
            )}

            {isSignUp && !needsPhoneForRegistration && (
              <div
                className={`rounded-xl border p-4 text-right transition-colors ${
                  tosAccepted
                    ? "border-green-300 bg-green-50"
                    : "border-amber-400 bg-amber-50 shadow-[0_0_0_3px_rgba(245,158,11,0.18)]"
                }`}
              >
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    checked={tosAccepted}
                    onCheckedChange={(checked) => {
                      setTosAccepted(checked === true);
                      setError(null);
                    }}
                    className="mt-0.5 size-6 shrink-0 rounded-md border-2 border-[#434AA7] bg-white shadow-[0_0_0_2px_#fff,0_0_0_4px_rgba(67,74,167,0.28)] data-[state=checked]:border-[#434AA7] data-[state=checked]:bg-[#434AA7] data-[state=checked]:text-white"
                  />
                  <span className="text-sm text-slate-600 leading-relaxed">
                    אני מסכים/ה ל
                    <Link
                      to="/terms-of-service"
                      className="text-blue-600 hover:text-blue-700 underline mx-1"
                      target="_blank"
                      onClick={(e) => e.stopPropagation()}
                    >
                      תנאי השימוש
                    </Link>
                    ול
                    <Link
                      to="/privacy-policy"
                      className="text-blue-600 hover:text-blue-700 underline mx-1"
                      target="_blank"
                      onClick={(e) => e.stopPropagation()}
                    >
                      מדיניות הפרטיות
                    </Link>
                  </span>
                </label>
                <p className="text-xs text-slate-500 mt-2">
                  חובה לפני הרשמה עם Google או אימייל.
                </p>
              </div>
            )}

            {showMethodChooser && !signupReady && (
              <p className="text-sm text-blue-900 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-right">
                {signupGateMessage()}
              </p>
            )}

            {showMethodChooser && (
              <div className="space-y-3">
                <p className="text-sm text-slate-500 text-right">בחרו איך ליצור את החשבון</p>
                <button
                  type="button"
                  onClick={handleGoogleSignIn}
                  disabled={isSubmitting || !signupReady}
                  className={`w-full h-12 rounded-xl border border-gray-200 bg-white flex items-center justify-center gap-3 text-slate-700 font-medium transition-shadow ${
                    isSubmitting || !signupReady
                      ? "opacity-45 cursor-not-allowed"
                      : "hover:shadow-md"
                  }`}
                >
                  <GoogleMark />
                  הירשם עם Google
                </button>
                <div className="relative flex items-center justify-center my-2">
                  <div className="border-t border-gray-200 flex-1" />
                  <span className="px-4 text-gray-500 text-sm font-medium">או</span>
                  <div className="border-t border-gray-200 flex-1" />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSignupMethod("email");
                    setError(null);
                  }}
                  disabled={isSubmitting || !signupReady}
                  className={`w-full h-12 rounded-xl text-white font-semibold shadow-[0_6px_16px_rgba(67,74,167,0.28)] transition-opacity ${
                    isSubmitting || !signupReady ? "opacity-45 cursor-not-allowed" : "hover:opacity-95"
                  }`}
                  style={{
                    background: "linear-gradient(90deg, #434AA7 0%, #4956D3 100%)",
                  }}
                >
                  הירשם עם אימייל וסיסמה
                </button>
              </div>
            )}

            {emailPath && (
              <>
                <p className="text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-right">
                  שם מלא, אימייל וסיסמה הם שדות חובה.
                </p>
                <div className="space-y-2">
                  <Label htmlFor="fullName" className="text-right block font-medium">
                    שם מלא
                    <span className="text-red-600 font-bold ms-1">*</span>
                    <span className="text-red-600 text-xs font-medium ms-1">חובה</span>
                  </Label>
                  <Input
                    id="fullName"
                    type="text"
                    placeholder="השם המלא"
                    value={formData.fullName}
                    onChange={(e) => handleInputChange("fullName", e.target.value)}
                    required
                    className={inputClass}
                    dir="rtl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-right block font-medium">
                    כתובת מייל
                    <span className="text-red-600 font-bold ms-1">*</span>
                    <span className="text-red-600 text-xs font-medium ms-1">חובה</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="הכנס את האימייל שלך"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    required
                    className={inputClass}
                    dir="rtl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-right block font-medium">
                    סיסמה
                    <span className="text-red-600 font-bold ms-1">*</span>
                    <span className="text-red-600 text-xs font-medium ms-1">חובה</span>
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="בחר סיסמא"
                      value={formData.password}
                      onChange={(e) => handleInputChange("password", e.target.value)}
                      required
                      className={`${inputClass} pr-10 md:pr-12`}
                      dir="rtl"
                      autoComplete="new-password"
                    />
                    <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}

            {!isSignUp && !needsPhoneForRegistration && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-right block font-medium">
                    כתובת מייל
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="הכנס את האימייל שלך"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    required
                    className={inputClass}
                    dir="rtl"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label htmlFor="password" className="text-right block font-medium">
                      סיסמה
                    </Label>
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(!showForgotPassword)}
                      className="text-blue-600 hover:text-blue-700 text-sm underline font-medium"
                    >
                      שכחת סיסמה?
                    </button>
                  </div>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="הסיסמה שלך"
                      value={formData.password}
                      onChange={(e) => handleInputChange("password", e.target.value)}
                      required
                      className={`${inputClass} pr-10 md:pr-12`}
                      dir="rtl"
                      autoComplete="current-password"
                    />
                    <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                </div>
                {showForgotPassword && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                    <p className="text-sm text-gray-700 text-right">
                      הזן את כתובת האימייל שלך ונשלח לך קישור לאיפוס הסיסמה
                    </p>
                    <Button
                      type="button"
                      onClick={handlePasswordReset}
                      disabled={isSubmitting}
                      className="w-full h-10 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium text-sm"
                    >
                      {isSubmitting ? "שולח..." : "שלח קישור לאיפוס סיסמה"}
                    </Button>
                  </div>
                )}
              </>
            )}

            {error && <div className="text-red-500 text-sm text-right bg-red-50 p-3 rounded-md">{error}</div>}
            {successMessage && (
              <div className="text-green-600 text-sm text-right bg-green-50 p-3 rounded-md">{successMessage}</div>
            )}

            {(needsPhoneForRegistration || emailPath || !isSignUp) && (
              <Button
                type="submit"
                className="w-full h-12 rounded-xl gradient-button text-white font-medium text-base hover:opacity-90 transition-opacity"
                disabled={
                  isSubmitting ||
                  (needsPhoneForRegistration && !phoneOk) ||
                  (emailPath && !signupReady)
                }
                style={{
                  background: "linear-gradient(90deg, #434AA7 0%, #4956D3 100%)",
                }}
              >
                {isSubmitting
                  ? "מתחבר..."
                  : needsPhoneForRegistration
                    ? "שמור והמשך"
                    : isSignUp
                      ? "צור חשבון"
                      : "התחבר"}
              </Button>
            )}

            {emailPath && (
              <button
                type="button"
                onClick={() => setSignupMethod("")}
                className="w-full text-center text-blue-600 hover:text-blue-700 text-sm underline font-medium"
              >
                בחרו שיטה אחרת
              </button>
            )}

            {!needsPhoneForRegistration && !isSignUp && (
              <>
                <div className="relative flex items-center justify-center my-8">
                  <div className="border-t border-gray-200 flex-1" />
                  <span className="px-4 text-gray-500 text-sm font-medium">או</span>
                  <div className="border-t border-gray-200 flex-1" />
                </div>
                <button
                  type="button"
                  onClick={handleGoogleSignIn}
                  disabled={isSubmitting}
                  className={`w-full h-12 rounded-xl border border-gray-200 bg-white flex items-center justify-center gap-3 text-slate-700 font-medium transition-shadow ${
                    isSubmitting ? "opacity-50 cursor-not-allowed" : "hover:shadow-md"
                  }`}
                >
                  <GoogleMark />
                  התחבר עם Google
                </button>
              </>
            )}

            {!needsPhoneForRegistration && (
              <div className="text-center pt-4">
                <button
                  type="button"
                  onClick={() => resetAuthMode(!isSignUp)}
                  className="text-blue-600 hover:text-blue-700 text-sm underline font-medium"
                >
                  {isSignUp ? "כבר יש לך חשבון? התחבר כאן" : "אין לך חשבון? הירשם כאן"}
                </button>
              </div>
            )}
          </form>
        </div>
      </div>

      <div className="hidden lg:flex lg:w-3/5 relative bg-gradient-to-br from-blue-100 to-blue-300 p-4 lg:p-8">
        <div className="flex flex-col justify-center items-center w-full relative">
          <div
            className="rounded-lg mb-8 overflow-hidden max-w-full"
            style={{ width: "min(900px, 100%)", height: "min(900px, 70vh)" }}
          >
            <img
              src="roman_statue_with_laptop.png"
              alt="Roman statue with laptop representing legal technology"
              className="w-full h-full object-cover"
            />
          </div>

          <div className="absolute top-20 right-20 bg-gradient-to-br from-blue-50 to-blue-100 backdrop-blur-sm rounded-2xl p-4 shadow-lg max-w-xs border border-blue-200/50">
            <div className="flex items-start gap-3">
              <p className="text-sm text-gray-700 text-right flex-1 leading-relaxed">
                מהם התנאים לקיומה של עוולת התקיפה לפי פקודת הנזיקין?
              </p>
              <HelpCircle className="w-5 h-5 text-violet-950 opacity-50 flex-shrink-0 mt-0.5" />
            </div>
          </div>

          <div className="absolute top-40 left-16 bg-gradient-to-br from-blue-50 to-blue-100 backdrop-blur-sm rounded-2xl p-4 shadow-lg max-w-xs border border-blue-200/50">
            <div className="flex items-start gap-3">
              <p className="text-sm text-gray-700 text-right flex-1 leading-relaxed">
                האם לפי כללי לשכת עורכי הדין, מותר לעורך דין להקליט שיחה עם עורך דין אחר?
              </p>
              <HelpCircle className="w-5 h-5 text-violet-950 opacity-50 flex-shrink-0 mt-0.5" />
            </div>
          </div>

          <div className="absolute bottom-32 right-12 bg-gradient-to-br from-blue-50 to-blue-100 backdrop-blur-sm rounded-2xl p-4 shadow-lg max-w-xs border border-blue-200/50">
            <div className="flex items-start gap-3">
              <p className="text-sm text-gray-700 text-right flex-1 leading-relaxed">
                האם שעות טיסה לחו&quot;ל נחשבות ל&quot;שעות עבודה&quot;?
              </p>
              <HelpCircle className="w-5 h-5 text-violet-950 opacity-50 flex-shrink-0 mt-0.5" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
